/**
 * Predefiniowana biblioteka warstw WMS/WMTS do szybkiego dodania na mapę.
 *
 * Służy dwóm miejscom:
 *  1. `AddWmsDialog` — tryb "Wybierz z katalogu" obok opcji wpisywania
 *     własnego adresu URL
 *  2. `EditProcedurePage` — admin ustawia które warstwy z katalogu są
 *     domyślnie dodane dla konkretnej procedury
 *
 * Kolejność w pliku: najpierw warstwy WMS (grupowane tematycznie),
 * potem warstwy WMTS (GUGIK referencyjne). Wewnątrz grupy warstwy
 * w kolejności przydatności dla urzędnika/mieszkańca.
 *
 * `id` — unikalny klucz w katalogu, używany jako `custom:${id}` w
 * `layerOrder`. Nie zmieniać dla istniejących wpisów (zepsuje
 * zapisane konfiguracje w DB/localStorage).
 */

export type CatalogLayerType = "wms" | "wmts";

export type CatalogLayer = {
  /** Unikalny ID w katalogu — nie zmieniać dla istniejących wpisów. */
  id: string;
  /** Nazwa wyświetlana w UI. */
  title: string;
  /** Typ protokołu — wms (GetMap) lub wmts (tiled matrix set). */
  type: CatalogLayerType;
  /** Endpoint URL (bez query string — parametry doklejone przez OL/fetch). */
  url: string;
  /**
   * Identyfikator warstwy:
   *  - WMS: wartość parametru `LAYERS=` w GetMap (np. "dzialki", "5")
   *  - WMTS: `Layer/Identifier` z GetCapabilities (np. "ORTOFOTOMAPA")
   */
  layerName: string;
  /** Grupa tematyczna — używana do grupowania w UI katalogu. */
  group: string;
};

// ─────────────────────────────────────────────────────────────────────────
// WMS — warstwy referencyjne i tematyczne
// ─────────────────────────────────────────────────────────────────────────

const WMS_LAYERS: CatalogLayer[] = [
  // Działki — geoportal
  { id: "gugik_dzialki_numery", title: "Numery działek", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow",
    layerName: "numery_dzialek", group: "Działki — geoportal" },
  { id: "gugik_dzialki", title: "Działki ewidencyjne z powiatów", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow",
    layerName: "dzialki", group: "Działki — geoportal" },
  { id: "gugik_budynki", title: "Budynki", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow",
    layerName: "budynki", group: "Działki — geoportal" },
  { id: "gugik_uzytki", title: "Użytki gruntowe", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow",
    layerName: "uzytki", group: "Działki — geoportal" },
  { id: "gugik_kontury", title: "Kontury klasyfikacyjne", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow",
    layerName: "kontury", group: "Działki — geoportal" },

  // Adresy (WMS)
  { id: "gugik_prg_adresy", title: "Punkty adresowe (PRG)", type: "wms",
    url: "https://mapy.geoportal.gov.pl/wss/ext/KrajowaIntegracjaNumeracjiAdresowej",
    layerName: "prg-adresy", group: "Adresy" },
  { id: "gugik_prg_ulice", title: "Ulice (PRG)", type: "wms",
    url: "https://mapy.geoportal.gov.pl/wss/ext/KrajowaIntegracjaNumeracjiAdresowej",
    layerName: "prg-ulice", group: "Adresy" },

  // Zagrożenie powodzią — 10% (raz na 10 lat)
  { id: "isok_mzp10_obszar", title: "Obszar szczególnego zagrożenia powodzią (10 lat)", type: "wms",
    url: "https://wody.isok.gov.pl/gpservices/KZGW/MZP20_Glebokosc_WysokiePrawdopodPowodzi/MapServer/WMSServer",
    layerName: "2", group: "Zagrożenie powodzią — raz na 10 lat" },
  { id: "isok_mzp10_wal", title: "Wał przeciwpowodziowy, zapora boczna (10 lat)", type: "wms",
    url: "https://wody.isok.gov.pl/gpservices/KZGW/MZP20_Glebokosc_WysokiePrawdopodPowodzi/MapServer/WMSServer",
    layerName: "5", group: "Zagrożenie powodzią — raz na 10 lat" },
  { id: "isok_mzp10_glebokosc", title: "Głębokość wody [m] (10 lat)", type: "wms",
    url: "https://wody.isok.gov.pl/gpservices/KZGW/MZP20_Glebokosc_WysokiePrawdopodPowodzi/MapServer/WMSServer",
    layerName: "1", group: "Zagrożenie powodzią — raz na 10 lat" },

  // Zagrożenie powodzią — 1% (raz na 100 lat)
  { id: "isok_mzp100_obszar", title: "Obszar szczególnego zagrożenia powodzią (100 lat)", type: "wms",
    url: "https://wody.isok.gov.pl/gpservices/KZGW/MZP20_Glebokosc_SredniePrawdopodPowodzi/MapServer/WMSServer",
    layerName: "17", group: "Zagrożenie powodzią — raz na 100 lat" },
  { id: "isok_mzp100_wal", title: "Wał przeciwpowodziowy, zapora boczna (100 lat)", type: "wms",
    url: "https://wody.isok.gov.pl/gpservices/KZGW/MZP20_Glebokosc_SredniePrawdopodPowodzi/MapServer/WMSServer",
    layerName: "20", group: "Zagrożenie powodzią — raz na 100 lat" },
  { id: "isok_mzp100_glebokosc", title: "Głębokość wody [m] (100 lat)", type: "wms",
    url: "https://wody.isok.gov.pl/gpservices/KZGW/MZP20_Glebokosc_SredniePrawdopodPowodzi/MapServer/WMSServer",
    layerName: "16", group: "Zagrożenie powodzią — raz na 100 lat" },

  // Złoża (MIDAS)
  { id: "midas_zloza", title: "MIDAS — granice złóż", type: "wms",
    url: "https://cbdgmapa.pgi.gov.pl/arcgis/services/midas/MapServer/WMSServer",
    layerName: "5", group: "Złoża" },
  { id: "midas_obszary_gornicze", title: "MIDAS — obszary górnicze", type: "wms",
    url: "https://cbdgmapa.pgi.gov.pl/arcgis/services/midas/MapServer/WMSServer",
    layerName: "4", group: "Złoża" },
  { id: "midas_tereny_gornicze", title: "MIDAS — tereny górnicze", type: "wms",
    url: "https://cbdgmapa.pgi.gov.pl/arcgis/services/midas/MapServer/WMSServer",
    layerName: "3", group: "Złoża" },
  { id: "midas_wybilansowane", title: "MIDAS — złoża wybilansowane", type: "wms",
    url: "https://cbdgmapa.pgi.gov.pl/arcgis/services/midas/MapServer/WMSServer",
    layerName: "2", group: "Złoża" },

  // Tereny osuwisk (SOPO)
  { id: "sopo_granice", title: "Granice osuwisk", type: "wms",
    url: "https://cbdgmapa.pgi.gov.pl/arcgis/services/geozagrozenia/sopo_obszary/MapServer/WMSServer",
    layerName: "2", group: "Tereny osuwisk" },
  { id: "sopo_osuwiska", title: "Osuwiska", type: "wms",
    url: "https://cbdgmapa.pgi.gov.pl/arcgis/services/geozagrozenia/sopo_obszary/MapServer/WMSServer",
    layerName: "0", group: "Tereny osuwisk" },
  { id: "sopo_centroidy", title: "Osuwiska — centroidy", type: "wms",
    url: "https://cbdgmapa.pgi.gov.pl/arcgis/services/geozagrozenia/sopo_obszary/MapServer/WMSServer",
    layerName: "3", group: "Tereny osuwisk" },
  { id: "sopo_zagrozenia", title: "Tereny zagrożone ruchami masowymi", type: "wms",
    url: "https://cbdgmapa.pgi.gov.pl/arcgis/services/geozagrozenia/sopo_obszary/MapServer/WMSServer",
    layerName: "1", group: "Tereny osuwisk" },

  // Ochrona przyrody (GDOŚ)
  { id: "gdos_soo", title: "Natura 2000 Specjalne Obszary Ochrony", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:SpecjalneObszaryOchrony",
    group: "Ochrona przyrody" },
  { id: "gdos_oso", title: "Natura 2000 Obszary Specjalnej Ochrony", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:ObszarySpecjalnejOchrony",
    group: "Ochrona przyrody" },
  { id: "gdos_ochr_krajobrazu", title: "Obszary Chronionego Krajobrazu", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:ObszaryChronionegoKrajobrazu",
    group: "Ochrona przyrody" },
  { id: "gdos_parki_krajobrazowe", title: "Parki krajobrazowe", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:ParkiKrajobrazowe",
    group: "Ochrona przyrody" },
  { id: "gdos_parki_narodowe", title: "Parki narodowe", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:ParkiNarodowe",
    group: "Ochrona przyrody" },
  { id: "gdos_pomniki", title: "Pomniki przyrody", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:PomnikiPrzyrody",
    group: "Ochrona przyrody" },
  { id: "gdos_rezerwaty", title: "Rezerwaty", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:Rezerwaty",
    group: "Ochrona przyrody" },
  { id: "gdos_stan_dok", title: "Stanowiska dokumentacyjne", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:StanowiskaDokumentacyjne",
    group: "Ochrona przyrody" },
  { id: "gdos_uzytki_eko", title: "Użytki ekologiczne", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:UzytkiEkologiczne",
    group: "Ochrona przyrody" },
  { id: "gdos_zpk", title: "Zespoły przyrodniczo-krajobrazowe", type: "wms",
    url: "http://sdi.gdos.gov.pl/wms", layerName: "GDOS:ZespolyPrzyrodniczoKrajobrazowe",
    group: "Ochrona przyrody" },

  // Uzbrojenie terenu (KIUT)
  { id: "kiut_wodociag", title: "Sieć wodociągowa", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu",
    layerName: "przewod_wodociagowy", group: "Uzbrojenie terenu" },
  { id: "kiut_kanalizacja", title: "Sieć kanalizacyjna", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu",
    layerName: "przewod_kanalizacyjny", group: "Uzbrojenie terenu" },
  { id: "kiut_elektro", title: "Sieć elektroenergetyczna", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu",
    layerName: "przewod_elektroenergetyczny", group: "Uzbrojenie terenu" },
  { id: "kiut_gaz", title: "Sieć gazowa", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu",
    layerName: "przewod_gazowy", group: "Uzbrojenie terenu" },
  { id: "kiut_cieplo", title: "Sieć ciepłownicza", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu",
    layerName: "przewod_cieplowniczy", group: "Uzbrojenie terenu" },
  { id: "kiut_telekom", title: "Sieć telekomunikacyjna", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu",
    layerName: "przewod_telekomunikacyjny", group: "Uzbrojenie terenu" },
  { id: "kiut_specjalny", title: "Sieć specjalna", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu",
    layerName: "przewod_specjalny", group: "Uzbrojenie terenu" },
  { id: "kiut_niezident", title: "Sieci niezidentyfikowane", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu",
    layerName: "przewod_niezidentyfikowany", group: "Uzbrojenie terenu" },
  { id: "kiut_urzadzenia", title: "Urządzenia", type: "wms",
    url: "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu",
    layerName: "przewod_urzadzenia", group: "Uzbrojenie terenu" },
];

// ─────────────────────────────────────────────────────────────────────────
// WMTS — tiled matrix sets (GUGIK referencyjne, ortofotomapy, topografia)
// ─────────────────────────────────────────────────────────────────────────

const WMTS_LAYERS: CatalogLayer[] = [
  { id: "gugik_wmts_hipso", title: "Cieniowanie i hipsometria", type: "wmts",
    url: "https://mapy.geoportal.gov.pl/wss/service/PZGIK/NMT/GRID1/WMTS/HypsometryAndShadedRelief",
    layerName: "HipsoCien", group: "WMTS — rzeźba terenu" },
  { id: "gugik_wmts_cieniowanie", title: "Cieniowanie", type: "wmts",
    url: "https://mapy.geoportal.gov.pl/wss/service/PZGIK/NMT/GRID1/WMTS/ShadedRelief",
    layerName: "Cieniowanie", group: "WMTS — rzeźba terenu" },
  { id: "gugik_wmts_orto_std", title: "Ortofotomapa standardowa", type: "wmts",
    url: "https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMTS/StandardResolution",
    layerName: "ORTOFOTOMAPA", group: "WMTS — ortofotomapa" },
  { id: "gugik_wmts_orto_hi", title: "Ortofotomapa wysokiej rozdzielczości", type: "wmts",
    url: "https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMTS/HighResolution",
    layerName: "ORTOFOTOMAPA", group: "WMTS — ortofotomapa" },
  { id: "gugik_wmts_bdot", title: "Baza Danych Obiektów Topograficznych (BDOT10k)", type: "wmts",
    url: "https://mapy.geoportal.gov.pl/wss/service/WMTS/guest/wmts/BDOT10k",
    layerName: "BDOT10k", group: "WMTS — mapy topograficzne" },
  { id: "gugik_wmts_topo", title: "Rastrowa Mapa Topograficzna Polski", type: "wmts",
    url: "https://mapy.geoportal.gov.pl/wss/service/WMTS/guest/wmts/TOPO",
    layerName: "MAPA TOPOGRAFICZNA", group: "WMTS — mapy topograficzne" },
];

/** Pełny katalog — najpierw WMS (tematyczne), potem WMTS (referencyjne). */
export const WMS_CATALOG: CatalogLayer[] = [...WMS_LAYERS, ...WMTS_LAYERS];

/** Zwraca katalog pogrupowany po `group`, zachowując kolejność zdefiniowaną wyżej. */
export function groupCatalog(): Array<{ group: string; layers: CatalogLayer[] }> {
  const groups: Array<{ group: string; layers: CatalogLayer[] }> = [];
  const byGroup = new Map<string, CatalogLayer[]>();
  for (const layer of WMS_CATALOG) {
    let arr = byGroup.get(layer.group);
    if (!arr) {
      arr = [];
      byGroup.set(layer.group, arr);
      groups.push({ group: layer.group, layers: arr });
    }
    arr.push(layer);
  }
  return groups;
}

/** Lookup po ID — używane gdy w payload/localStorage zapisany jest tylko ID z katalogu. */
export function findCatalogLayer(id: string): CatalogLayer | null {
  return WMS_CATALOG.find((l) => l.id === id) ?? null;
}

/**
 * Stabilny ID dla custom warstwy — wyliczany deterministycznie z
 * `catalogId` (jeśli z katalogu) lub z URL+layerName (własny adres).
 * Dzięki temu `defaultLayerOrder` zapisany w payload/localStorage działa
 * między sesjami (po hydratacji z payload warstwa ma ten sam ID i
 * `resolveRenderOrder` odnajduje ją w zapisanej kolejności).
 *
 * Format:
 *  - z katalogu: `cat:gugik_dzialki_numery`
 *  - własny adres: `url:<base64 z url|layerName, 20 znaków>`
 */
export function stableCustomLayerId(input: {
  catalogId?: string;
  url: string;
  layerName: string;
}): string {
  if (input.catalogId) return `cat:${input.catalogId}`;
  // Deterministyczny ID z URL + layerName. UWAGA: wcześniej było to 20
  // pierwszych znaków base64 z `url|layerName` — czyli praktycznie sam
  // początek URL-a. Dwie warstwy z tego samego serwera (typowe: studium i
  // plan z geoportalu gminy) dostawały IDENTYCZNY ID → druga była traktowana
  // jak duplikat pierwszej i znikała z listy/kolejności. Teraz: hash FNV-1a
  // z całego napisu (32-bit, base36) + krótki prefiks dla czytelności.
  // Zmiana ID unieważnia stare wpisy `custom:url:…` w `defaultLayerOrder`
  // (warstwa ląduje na końcu kolejności, nic więcej) — akceptowalne.
  const raw = `${input.url}|${input.layerName}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < raw.length; i++) {
    h ^= raw.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  const prefix = input.layerName.replace(/[^a-zA-Z0-9]+/g, "").slice(0, 12) || "wms";
  return `url:${prefix}-${h.toString(36)}`;
}
